import {MOON_350, TEAL_500} from '@wandb/weave/common/css/color.styles';
import {formatDurationWithColons} from '@wandb/weave/common/util/time';
import {Button} from '@wandb/weave/components/Button';
import {LoadingDots} from '@wandb/weave/components/LoadingDots';
import {useWFHooks} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import WaveSurfer from 'wavesurfer.js';

import {downloadFromUrl} from './Shared';
import {ContentViewMetadataLoadedProps} from './types';

export const AudioContent = (
  props: ContentViewMetadataLoadedProps & {transparentBackground?: boolean}
) => {
  const {entity, project, content, metadata, transparentBackground} = props;
  const {useFileContent} = useWFHooks();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  // Memoize the params to prevent unnecessary re-queries
  const fileContentParams = useMemo(
    () => ({
      entity,
      project,
      digest: content,
    }),
    [entity, project, content]
  );

  const audioBinary = useFileContent(fileContentParams);

  useMemo(() => {
    if (audioBinary.result) {
      setAudioUrl(URL.createObjectURL(new Blob([audioBinary.result])));
    }
  }, [audioBinary.result]);

  if (audioBinary.loading) {
    return <LoadingDots />;
  } else if (audioBinary.result == null || audioUrl == null) {
    return <span></span>;
  }

  const downloadFile = () => {
    if (!audioUrl) {
      console.error('Audio URL is not available');
      return;
    }
    downloadFromUrl(audioUrl, metadata.filename);
  };

  return (
    <MiniAudioViewer
      audioSrc={audioUrl}
      height={24}
      downloadFile={downloadFile}
      transparentBackground={transparentBackground}
    />
  );
};

const SLIDER_WIDTH_THRESHOLD = 180;
const MINI_WIDTH_THRESHOLD = 80;

enum ShowMode {
  Controls = 'controls',
  Slider = 'slider',
  Mini = 'mini',
}

// Imperative controls exposed to callers (e.g. the agent "play all" playlist)
// so playback can be driven from outside the waveform's own buttons.
export interface AudioClipHandle {
  // Resume playback from the current position.
  play: () => void;
  // Restart playback from the beginning of the clip.
  playFromStart: () => void;
  pause: () => void;
}

export const MiniAudioViewer = forwardRef<
  AudioClipHandle,
  {
    audioSrc: string;
    height: number;
    downloadFile?: () => void;
    transparentBackground?: boolean;
    // Fired when the clip finishes playing on its own.
    onFinish?: () => void;
    // Fired when wavesurfer fails to load/decode audioSrc (e.g. a cross-origin
    // fetch rejected by CORS), so the caller can fall back to a download link.
    onError?: () => void;
  }
>(
  (
    {audioSrc, height, downloadFile, transparentBackground, onFinish, onError},
    ref
  ) => {
    /*
  Heavily inspired by code in weave-js/src/components/Panel2/AudioViewer.tsx
  This component has 3 modes, based on the width of the container. Component
  is responsive to width changes. Modes:
  - controls: show play/pause, time. ex:                ([>] 0:00/0:05)
  - slider: show all. ex:                               ([>] 0:01/0:05 --|-----------  [v])
  - mini: show play/pause ex:                           ([>])
  */
    const measureDivRef = useRef<HTMLDivElement>(null);
    // Track container width reactively so the layout mode responds to column
    // resizing — a one-shot offsetWidth read never updates.
    const [containerWidth, setContainerWidth] = useState(0);

    const wavesurferRef = useRef<WaveSurfer>(undefined);
    const waveformDomRef = useRef<HTMLDivElement>(null);
    const [audioLoading, setAudioLoading] = React.useState(true);
    const [audioPlaying, setAudioPlaying] = React.useState(false);
    const [audioTotalTime, setAudioTotalTime] = React.useState<number>();
    const [audioCurrentTime, setAudioCurrentTime] = React.useState<number>();

    // Latest values read inside the setup effect without re-running it (which
    // would re-decode the audio and lose playback position).
    const onFinishRef = useRef(onFinish);
    onFinishRef.current = onFinish;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    const heightRef = useRef(height);
    heightRef.current = height;

    // Tracks wavesurfer readiness via a ref (not state) so imperative controls
    // can read it synchronously without stale-closure races.
    const isReadyRef = useRef(false);
    // A play request that arrived before the waveform was ready; honored on ready.
    const pendingPlayRef = useRef<'play' | 'playFromStart' | null>(null);

    useImperativeHandle(
      ref,
      () => ({
        play: () => {
          const ws = wavesurferRef.current;
          if (!ws) {
            return;
          }
          if (!isReadyRef.current) {
            pendingPlayRef.current = 'play';
            return;
          }
          ws.play();
        },
        playFromStart: () => {
          const ws = wavesurferRef.current;
          if (!ws) {
            return;
          }
          if (!isReadyRef.current) {
            pendingPlayRef.current = 'playFromStart';
            return;
          }
          ws.seekTo(0);
          ws.play();
        },
        pause: () => {
          wavesurferRef.current?.pause();
        },
      }),
      []
    );

    // Create the wavesurfer instance only when the source changes. Dimension
    // changes are applied to the existing instance below rather than recreating
    // it, so a resize never re-decodes the clip or drops playback position.
    useEffect(() => {
      if (audioSrc && waveformDomRef.current && measureDivRef.current) {
        const wavesurfer = WaveSurfer.create({
          backend: 'WebAudio',
          container: waveformDomRef.current,
          waveColor: MOON_350,
          progressColor: TEAL_500,
          cursorColor: MOON_350,
          responsive: true,
          height: heightRef.current,
          barHeight: 1.3, // slightly exaggerated
          hideScrollbar: true,
        });

        wavesurferRef.current = wavesurfer;

        /* WAVESURFER EVENTS */
        wavesurfer.on('play', () => {
          setAudioPlaying(true);
        });
        wavesurfer.on('pause', () => {
          setAudioPlaying(false);
        });
        wavesurfer.on('ready', () => {
          const duration = wavesurfer!.getDuration();

          isReadyRef.current = true;
          setAudioLoading(false);
          setAudioCurrentTime(undefined);
          setAudioTotalTime(duration || undefined);

          // Honor a play request that arrived before the clip was ready.
          const pending = pendingPlayRef.current;
          if (pending) {
            pendingPlayRef.current = null;
            if (pending === 'playFromStart') {
              wavesurfer.seekTo(0);
            }
            wavesurfer.play();
          }
        });
        // fires when playback reaches the end of the clip
        wavesurfer.on('finish', () => {
          onFinishRef.current?.();
        });
        // fires when the CORS-gated WebAudio fetch/decode of audioSrc fails
        wavesurfer.on('error', () => {
          setAudioLoading(false);
          onErrorRef.current?.();
        });
        // fires when you click a new location in the waveform
        wavesurfer.on('seek', () => {
          setAudioCurrentTime(wavesurfer!.getCurrentTime());
        });
        // fires continuously while audio is playing
        wavesurfer.on('audioprocess', () => {
          setAudioCurrentTime(wavesurfer!.getCurrentTime());
        });

        wavesurfer.load(audioSrc);
      }

      return () => {
        isReadyRef.current = false;
        pendingPlayRef.current = null;
        if (wavesurferRef.current) {
          wavesurferRef.current.destroy();
        }
      };
    }, [audioSrc]);

    // Apply dimension changes (height, container width) to the existing
    // wavesurfer without recreating it. setHeight resizes and drawBuffer
    // re-renders from the already-decoded buffer; gated on load so it no-ops
    // until there is a buffer to draw.
    useEffect(() => {
      const ws = wavesurferRef.current;
      if (ws == null || audioLoading) {
        return;
      }
      ws.setHeight(height);
      ws.drawBuffer();
    }, [height, containerWidth, audioLoading]);

    const audioCurrentTimeStr = [audioCurrentTime, audioTotalTime]
      .map(formatDurationWithColons)
      .map(x => (x.slice(0, 1) === '0' ? x.slice(1) : x))
      .join('/');

    useEffect(() => {
      const el = measureDivRef.current;
      if (el == null || typeof ResizeObserver === 'undefined') {
        return;
      }
      const observer = new ResizeObserver(entries => {
        setContainerWidth(entries[0]?.contentRect.width ?? el.offsetWidth);
      });
      observer.observe(el);
      return () => observer.disconnect();
    }, []);

    const showMode =
      containerWidth > SLIDER_WIDTH_THRESHOLD
        ? ShowMode.Slider
        : containerWidth > 0 && containerWidth < MINI_WIDTH_THRESHOLD
          ? ShowMode.Mini
          : ShowMode.Controls;

    return (
      <Tailwind style={{width: '100%'}}>
        <div ref={measureDivRef} className="w-full px-8 py-8">
          <div
            className={`rounded-2xl ${
              transparentBackground ? '' : 'bg-moon-150'
            } ${showMode === ShowMode.Slider ? 'w-full' : 'w-fit'}`}>
            <div className="flex w-full items-center">
              <Button
                className={`pl-1 pr-1 ${
                  showMode === ShowMode.Mini ? 'ml-0' : 'ml-6'
                }`}
                disabled={audioLoading}
                icon={audioPlaying ? 'pause' : 'play'}
                onClick={() => {
                  if (wavesurferRef.current) {
                    wavesurferRef.current.playPause();
                  }
                }}
                size={showMode === ShowMode.Mini ? 'medium' : 'small'}
                variant="ghost"
              />
              {showMode !== ShowMode.Mini && (
                <div
                  className={`text-s pl-4 ${
                    showMode === ShowMode.Slider ? 'pr-4' : 'pr-10'
                  }`}>
                  {audioCurrentTimeStr}
                </div>
              )}
              {/* Waveform should always be mounted, but hidden when not in slider mode */}
              <div
                ref={waveformDomRef}
                className={`w-full overflow-hidden ${
                  showMode === ShowMode.Slider ? 'block' : 'hidden'
                }`}
              />
              <div>
                {showMode === ShowMode.Slider && downloadFile && (
                  <Button
                    icon="download"
                    onClick={downloadFile}
                    size="small"
                    variant="ghost"
                    className="mr-6"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </Tailwind>
    );
  }
);
MiniAudioViewer.displayName = 'MiniAudioViewer';
